import React, { useContext } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
// Utils
import {
  DASHBOARD,
  BU_ID,
  ANIMALS,
  GROUPS,
  CREATE_GROUP,
  CREATE_BATCH,
  EDIT_BATCH,
  EDIT_GROUP,
  LIVESTOCK,
  ANIMAL_VIEW,
  ANIMALS_ARCHIVE,
  GROUP_VIEW,
  FIELDS,
  CREATE_FIELD,
  EDIT_FIELD,
  FIELD_VIEW,
  BATCH_VIEW,
  TAGS,
  TAG_VIEW,
} from "constants/Routes";
import { BU_TYPES } from "trading/constants";
// Hooks
import { useHasFeature } from "hooks";
// Pages
import { Animals } from "pages/MyLivestock/Animals";
import { ArchiveAnimals } from "pages/MyLivestock/Archive";
import Groups from "pages/MyLivestock/Groups";
import Fields from "pages/MyLivestock/Fields";
import AnimalView from "pages/MyLivestock/Animals/AnimalView";
import GroupView from "pages/MyLivestock/Groups/GroupView";
import { Group } from "groups/Group";
import FieldView from "pages/MyLivestock/Fields/FieldView";
import CreateEditGroup from "pages/MyLivestock/Groups/CreateEditGroup";
import { CreateEditBatch } from "pages/MyLivestock/Groups/CreateEditBatch";
import { BatchView } from "pages/MyLivestock/Groups/BatchView";
import CreateEditField from "pages/MyLivestock/Fields/CreateEditField";
import { EditField } from "fields/EditField";

import { AppContext } from "config/appProvider";
import { EditGroup } from "groups/EditGroup";
import { Field } from "fields/Field";
import { Batch } from "groups/Batch";
import Tags from "tags/pages";
import { TagView } from "tags/pages/TagView";

const ROUTES_PATHS = {
  Livestock: `${BU_ID}${LIVESTOCK}`,
  Animals: `${BU_ID}${ANIMALS}`,
  AnimalView: `${BU_ID}${ANIMAL_VIEW}`,
  AnimalArchive: `${BU_ID}${ANIMALS_ARCHIVE}`,
  Groups: `${BU_ID}${GROUPS}`,
  CreateGroup: `${BU_ID}${CREATE_GROUP}`,
  EditGroup: `${BU_ID}${EDIT_GROUP}`,
  CreateBatch: `${BU_ID}${CREATE_BATCH}`,
  EditBatch: `${BU_ID}${EDIT_BATCH}`,
  GroupView: `${BU_ID}${GROUP_VIEW}/:groupId`,
  BatchView: `${BU_ID}${BATCH_VIEW}/:groupId`,
  Fields: `${BU_ID}${FIELDS}`,
  CreateField: `${BU_ID}${CREATE_FIELD}`,
  EditField: `${BU_ID}${EDIT_FIELD}`,
  FieldView: `${BU_ID}${FIELD_VIEW}/:fieldId`,
  Tags: `${BU_ID}${TAGS}`,
  TagView: `${BU_ID}${TAG_VIEW}/:tagId`,
};

export const PATHS = Object.keys(ROUTES_PATHS).map((key) => ROUTES_PATHS[key]);

const LivestockRoutes: React.FC = () => {
  const {
    mainAppState: { selectedBUType },
  } = useContext(AppContext);

  const isShowFields: boolean = useHasFeature("FIELDS");

  const isShowAnimalsForBuyer: boolean = useHasFeature("SHOW_ANIMALS_FOR_BUYER");
  const isShowAnimalsArchive: boolean = useHasFeature("ANIMALS_ARCHIVE");
  const useServerPaginatedAnimalsList = useHasFeature("SERVER_SIDE_PAGINATION_FOR_ANIMALS_LIST");

  const isFarmer: boolean = selectedBUType !== BU_TYPES.processor;

  const isAnimalsEnable: boolean = isFarmer || isShowAnimalsForBuyer;
  const isGroupsEnable: boolean = isAnimalsEnable;
  const isFieldsEnable: boolean = isShowFields && isAnimalsEnable;

  const animalsRoutes = [
    <Route exact path={ROUTES_PATHS.Animals} key={ROUTES_PATHS.Animals} component={Animals} />,
    <Route exact path={ROUTES_PATHS.AnimalView} key={ROUTES_PATHS.AnimalView} component={AnimalView} />,
    isShowAnimalsArchive && (
      <Route exact path={ROUTES_PATHS.AnimalArchive} key={ROUTES_PATHS.AnimalArchive} component={ArchiveAnimals} />
    ),
  ];

  const groupsRoutes = [
    <Route exact path={ROUTES_PATHS.Groups} key={ROUTES_PATHS.Groups} component={Groups} />,
    <Route exact path={ROUTES_PATHS.CreateGroup} key={ROUTES_PATHS.CreateGroup} component={CreateEditGroup} />,
    <Route
      exact
      path={ROUTES_PATHS.EditGroup}
      key={ROUTES_PATHS.EditGroup}
      component={useServerPaginatedAnimalsList ? EditGroup : CreateEditGroup}
    />,
    <Route exact path={ROUTES_PATHS.CreateBatch} key={ROUTES_PATHS.CreateBatch} component={CreateEditBatch} />,
    <Route
      exact
      path={ROUTES_PATHS.GroupView}
      key={ROUTES_PATHS.GroupView}
      component={useServerPaginatedAnimalsList ? Group : GroupView}
    />,
    <Route exact path={ROUTES_PATHS.EditBatch} key={ROUTES_PATHS.EditBatch} component={CreateEditBatch} />,
    <Route
      exact
      path={ROUTES_PATHS.BatchView}
      key={ROUTES_PATHS.BatchView}
      component={useServerPaginatedAnimalsList ? Batch : BatchView}
    />,
  ];

  const fieldsRoutes = [
    <Route exact path={ROUTES_PATHS.Fields} key={ROUTES_PATHS.Fields} component={Fields} />,
    <Route exact path={ROUTES_PATHS.CreateField} key={ROUTES_PATHS.CreateField} component={CreateEditField} />,
    <Route
      exact
      path={ROUTES_PATHS.EditField}
      key={ROUTES_PATHS.EditField}
      component={useServerPaginatedAnimalsList ? EditField : CreateEditField}
    />,
    <Route
      exact
      path={ROUTES_PATHS.FieldView}
      key={ROUTES_PATHS.FieldView}
      component={useServerPaginatedAnimalsList ? Field : FieldView}
    />,
  ];

  const tagsRoutes = [
    <Route key={ROUTES_PATHS.Tags} path={ROUTES_PATHS.Tags} component={Tags} />,

    <Route exact path={ROUTES_PATHS.TagView} key={ROUTES_PATHS.TagView} component={TagView} />,
  ];

  const redirects = [<Redirect key={ROUTES_PATHS.Livestock} from={LIVESTOCK} to={ANIMALS} exact />];

  return (
    <Route exact path={PATHS}>
      <Switch>
        {redirects}
        {isAnimalsEnable && animalsRoutes}
        {isGroupsEnable && groupsRoutes}
        {isFieldsEnable && fieldsRoutes}
        {tagsRoutes}
        <Redirect to={DASHBOARD} />
      </Switch>
    </Route>
  );
};

export default LivestockRoutes;
